const API_BASE = '/api/compliance/trust-portal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface TrustPortalSettings {
  id?: number;
  company_id?: number;
  slug: string;
  is_public: boolean;
  show_certifications: boolean;
  show_frameworks: boolean;
  show_compliance_score: boolean;
  show_last_audit_date: boolean;
  show_controls_count: boolean;
  show_emission_targets: boolean;
  show_scope_breakdown: boolean;
  show_carbon_credits: boolean;
  show_emission_offsets: boolean;
  show_sustainability_score: boolean;
  custom_headline?: string;
  custom_description?: string;
  accent_color?: string;
  created_at?: string;
  updated_at?: string;
}

export interface EmissionTarget {
  name: string;
  target_type: string;
  scope: string;
  baseline_year: number;
  target_year: number;
  baseline_value: number;
  target_value: number;
  current_value: number | null;
  intensity_metric: string | null;
  status: string;
  percent_achieved: number;
  on_track: boolean;
}

export interface ScopeBreakdown {
  scope1_total: number;
  scope2_total: number;
  scope3_total: number;
  grand_total: number;
  report_period: string | null;
  methodology: string | null;
}

export interface CarbonCreditsData {
  total_active: number;
  total_volume_tonnes: number;
  credits: Array<{
    name: string;
    credit_type: string;
    volume_tonnes: number;
    status: string;
    provider: string | null;
  }>;
}

export interface EmissionOffsetsData {
  total_credits: number;
  total_tco2e: number;
  verified_tco2e: number;
  retired_tco2e: number;
  by_registry: Array<{
    registry: string;
    count: number;
    total_tco2e: number;
  }>;
}

export interface TrustPortalPublicData {
  company_name: string;
  company_logo?: string;
  custom_headline?: string;
  custom_description?: string;
  accent_color?: string;
  show_certifications: boolean;
  show_frameworks: boolean;
  show_compliance_score: boolean;
  show_last_audit_date: boolean;
  show_controls_count: boolean;
  compliance_score?: number;
  certifications?: Array<{ name: string; certification_name?: string; issued_at: string; expires_at?: string; valid_until?: string }>;
  frameworks?: Array<{ name: string; framework_name?: string; status: string; controls_count?: number; compliance_score?: number }>;
  last_audit_date?: string;
  controls_count?: number;
  // Sustainability
  show_emission_targets?: boolean;
  show_scope_breakdown?: boolean;
  show_carbon_credits?: boolean;
  show_emission_offsets?: boolean;
  show_sustainability_score?: boolean;
  emission_targets?: EmissionTarget[];
  scope_breakdown?: ScopeBreakdown | null;
  carbon_credits?: CarbonCreditsData;
  emission_offsets?: EmissionOffsetsData;
  sustainability_score?: number | null;
}

export const trustPortalApi = {
  async getSettings(): Promise<TrustPortalSettings> {
    const res = await fetch(API_BASE, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async saveSettings(data: Partial<TrustPortalSettings>): Promise<TrustPortalSettings> {
    const res = await fetch(API_BASE, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getPublicPortal(slug: string): Promise<TrustPortalPublicData> {
    const res = await fetch(`${API_BASE}/public/${slug}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Not found' }));
      throw new Error(err.error || err.message || `API Error: ${res.status}`);
    }
    const result: any = await res.json();
    return result.data || result;
  },
};
